import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/corezoidApi';
import getApiBaseUrl from '../utils/apiBaseUrl';
import './Sidebar.css';

const API_BASE_URL = getApiBaseUrl();

const roleLabels = {
  trader: 'Trader',
  gateway: 'Gateway'
};

const menuGroups = [
  {
    title: 'Overview',
    items: [
      { path: '/dashboard', label: 'Dashboard', short: 'DB' }
    ]
  },
  {
    title: 'Orders',
    items: [
      { path: '/payin', label: 'PayIN', short: 'IN' },
      { path: '/payout', label: 'PayOUT', short: 'OUT' }
    ]
  },
  {
    title: 'Assets',
    items: [
      { path: '/requisites', label: 'Requisites', short: 'RQ' },
      { path: '/devices', label: 'Devices', short: 'DV' },
      { path: '/wallet', label: 'Wallet', short: 'WL' }
    ]
  },
  {
    title: 'Monitoring',
    items: [
      { path: '/messages', label: 'Messages', short: 'MS' }
    ]
  }
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [isOnline, setIsOnline] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('active');
  const visibleMenuGroups = user?.role === 'gateway'
    ? menuGroups.filter((group) => group.title === 'Orders')
    : menuGroups;

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('sidebar-open', isOpen);

    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [isOpen]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const loadStatus = async () => {
      try {
        const response = await apiClient.get(`${API_BASE_URL}/auth/status`);
        if (response.data.success) {
          const status = response.data.status.toLowerCase();
          setCurrentStatus(status);
          setIsOnline(status === 'active');
        }
      } catch (error) {
        console.error('Error loading status:', error);
        setCurrentStatus('active');
        setIsOnline(true);
      }
    };

    loadStatus();
  }, [user]);

  const handleToggleChange = async (checked) => {
    if (currentStatus === 'inactive') {
      return;
    }

    setIsUpdating(true);
    try {
      const status = checked ? 'active' : 'offline';
      const response = await apiClient.post(`${API_BASE_URL}/auth/status`, { status });

      if (response.data.success) {
        setIsOnline(checked);
        setCurrentStatus(status);
      } else {
        setIsOnline(!checked);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setIsOnline(!checked);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  const currentStatusLabel = currentStatus === 'inactive'
    ? 'Locked'
    : currentStatus === 'offline'
      ? 'Offline'
      : 'Online';

  return (
    <>
      <button
        className="sidebar-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
      >
        {isOpen ? 'Close' : 'Menu'}
      </button>
      {isOpen && <div className="sidebar-overlay active" onClick={closeSidebar} />}
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img
            className="sidebar-logo"
            src="/nadal-pay-logo.png?v=20261027"
            alt="Nadal Pay"
          />
        </div>

        {user && (
          <div className="sidebar-user-section">
            <div className="sidebar-user-card">
              <div>
                <div className="sidebar-user-role">
                  {roleLabels[user.role] || user.role || 'User'}
                </div>
                <div className="sidebar-user-name">{user.name || user.login}</div>
              </div>
              <div className={`sidebar-status-badge status-${currentStatus}`}>
                {currentStatusLabel}
              </div>
            </div>

            <div className="online-toggle-container">
              <div>
                <div className="online-toggle-label">Online ({currentStatusLabel})</div>
              </div>
              <label className="online-toggle">
                <input
                  type="checkbox"
                  checked={isOnline}
                  onChange={(e) => handleToggleChange(e.target.checked)}
                  disabled={isUpdating || currentStatus === 'inactive'}
                  title={currentStatus === 'inactive' ? 'Status is controlled by administrator' : ''}
                />
                <span className="online-toggle-slider" />
              </label>
            </div>
          </div>
        )}
        <nav className="sidebar-nav">
          {visibleMenuGroups.map((group) => (
            <div key={group.title} className="sidebar-group">
              <div className="sidebar-group-title">{group.title}</div>
              <ul className="sidebar-menu">
                {group.items.map((item) => (
                  <li key={item.path} className="sidebar-menu-item">
                    <Link
                      to={item.path}
                      className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                      onClick={closeSidebar}
                    >
                      <span className="sidebar-label">{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="sidebar-logout-button">
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
